import { supabase } from '../backend/supabaseClient';
import {
  calculateMLBalance,
  calculateFBBalance,
  calculateAlignment,
  calculateAlignmentStatus,
  SENSOR_CATEGORIES
} from '../processing/statisticsCalculator';

export const dataService = {
  currentSessionId: null,
  dataBuffer: [],
  isActive: false,
  BATCH_SIZE: 60,
  flushInterval: null,

  startSession: async (userId, sessionName) => {
    console.log('📊 dataService.startSession called');

    if (dataService.isActive) {
      console.log('⚠️ Session already active');
      return { id: dataService.currentSessionId };
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const insertData = {
        user_id: userId,
        session_name: sessionName || `Session ${new Date().toLocaleDateString()}`,
        started_at: new Date().toISOString(),
        status: 'active'
      };

      console.log('📤 Inserting:', insertData);

      const { data, error } = await supabase
        .from('sessions')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      dataService.currentSessionId = data.id;
      dataService.dataBuffer = [];
      dataService.isActive = true;

      if (dataService.flushInterval) {
        clearInterval(dataService.flushInterval);
      }
      dataService.flushInterval = setInterval(() => {
        dataService.flushBuffer();
      }, 10000);

      console.log('✅ Session started:', data.id);
      return data;
    } catch (error) {
      console.error('❌ startSession error:', error);
      throw error;
    }
  },

  endSession: async () => {
    if (!dataService.isActive || !dataService.currentSessionId) {
      return null;
    }

    try {
      if (dataService.flushInterval) {
        clearInterval(dataService.flushInterval);
        dataService.flushInterval = null;
      }

      await dataService.flushBuffer();

      const { data, error } = await supabase
        .from('sessions')
        .update({
          ended_at: new Date().toISOString(),
          status: 'completed'
        })
        .eq('id', dataService.currentSessionId)
        .select()
        .single();

      if (error) throw error;

      console.log('📊 Data session ended:', dataService.currentSessionId);

      dataService.currentSessionId = null;
      dataService.isActive = false;

      return data;
    } catch (error) {
      console.error('Failed to end session:', error);
      dataService.currentSessionId = null;
      dataService.isActive = false;
      throw error;
    }
  },

  isSessionActive: () => {
    return dataService.isActive && dataService.currentSessionId !== null;
  },

  calculateSectionAverages: (sensorData) => {
    const getAverage = (indices) => {
      const values = indices.map(i => sensorData[i + 1] || 0);
      return values.length > 0
        ? Math.round(values.reduce((a, b) => a + b, 0) / values.length)
        : 0;
    };

    return {
      toe: getAverage(SENSOR_CATEGORIES.PLATFORM),
      metatarsal: getAverage(SENSOR_CATEGORIES.METATARSAL),
      frontLeft: getAverage(SENSOR_CATEGORIES.FRONT_LEFT),
      frontMid: getAverage(SENSOR_CATEGORIES.FRONT_MID),
      frontRight: getAverage(SENSOR_CATEGORIES.FRONT_RIGHT),
      backLeft: getAverage(SENSOR_CATEGORIES.BACK_LEFT),
      backMid: getAverage(SENSOR_CATEGORIES.BACK_MID),
      backRight: getAverage(SENSOR_CATEGORIES.BACK_RIGHT),
    };
  },

  calculatePressureStats: (sensorData) => {
    const values = Object.values(sensorData).filter(v => typeof v === 'number');

    if (values.length === 0) {
      return { max: 0, std: 0, hotspotCount: 0, hotspotRegions: [] };
    }

    const max = Math.max(...values);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
    const std = Math.round(Math.sqrt(variance));

    const hotspotThreshold = Math.max(max * 0.8, 500);
    const hotspotRegions = new Set();

    Object.entries(sensorData).forEach(([key, value]) => {
      if (value >= hotspotThreshold) {
        const sensorIndex = parseInt(key) - 1;
        hotspotRegions.add(dataService.getSensorRegion(sensorIndex));
      }
    });

    return {
      max,
      std,
      hotspotCount: hotspotRegions.size,
      hotspotRegions: Array.from(hotspotRegions)
    };
  },

  getSensorRegion: (sensorIndex) => {
    if (SENSOR_CATEGORIES.PLATFORM.includes(sensorIndex)) return 'platform';
    if (SENSOR_CATEGORIES.METATARSAL.includes(sensorIndex)) return 'metatarsal';
    if (SENSOR_CATEGORIES.FRONT_LEFT.includes(sensorIndex)) return 'front_left';
    if (SENSOR_CATEGORIES.FRONT_MID.includes(sensorIndex)) return 'front_mid';
    if (SENSOR_CATEGORIES.FRONT_RIGHT.includes(sensorIndex)) return 'front_right';
    if (SENSOR_CATEGORIES.BACK_LEFT.includes(sensorIndex)) return 'back_left';
    if (SENSOR_CATEGORIES.BACK_MID.includes(sensorIndex)) return 'back_mid';
    if (SENSOR_CATEGORIES.BACK_RIGHT.includes(sensorIndex)) return 'back_right';
    return 'unknown';
  },

  addSnapshot: ({
    leftSensorData = {},
    rightSensorData = {},
    leftAverages = null,
    rightAverages = null,
    leftIMU = { accel: [0, 0, 0], gyro: [0, 0, 0] },
    rightIMU = { accel: [0, 0, 0], gyro: [0, 0, 0] },
    leftOnPointe = false,
    rightOnPointe = false,
    leftAlignment = null,
    leftAlignmentStatus = null,
    rightAlignment = null,
    rightAlignmentStatus = null,
    rollThroughs = 0,
    springs = 0,
    jumpLandings = 0,
    lastTransitionType = null,
    lastTransitionConfidence = null,
  }) => {
    if (!dataService.isActive || !dataService.currentSessionId) {
      console.warn('⚠️ addSnapshot called but no active session');
      return;
    }

    const leftSections = leftAverages || dataService.calculateSectionAverages(leftSensorData);
    const rightSections = rightAverages || dataService.calculateSectionAverages(rightSensorData);

    let leftAlignX, leftAlignY, leftAlignStat;
    let rightAlignX, rightAlignY, rightAlignStat;

    if (leftAlignment) {
      leftAlignX = Math.round(leftAlignment.x);
      leftAlignY = Math.round(leftAlignment.y);
      leftAlignStat = leftAlignmentStatus;
    } else {
      const leftAlign = calculateAlignment(
        calculateMLBalance(leftSensorData),
        calculateFBBalance(leftSensorData)
      );
      leftAlignX = Math.round(leftAlign.x);
      leftAlignY = Math.round(leftAlign.y);
      leftAlignStat = calculateAlignmentStatus(leftAlign, leftOnPointe);
    }

    if (rightAlignment) {
      rightAlignX = Math.round(rightAlignment.x);
      rightAlignY = Math.round(rightAlignment.y);
      rightAlignStat = rightAlignmentStatus;
    } else {
      const rightAlign = calculateAlignment(
        calculateMLBalance(rightSensorData),
        calculateFBBalance(rightSensorData)
      );
      rightAlignX = Math.round(rightAlign.x);
      rightAlignY = Math.round(rightAlign.y);
      rightAlignStat = calculateAlignmentStatus(rightAlign, rightOnPointe);
    }

    const leftPressure = dataService.calculatePressureStats(leftSensorData);
    const rightPressure = dataService.calculatePressureStats(rightSensorData);

    const record = {
      session_id: dataService.currentSessionId,
      timestamp: new Date().toISOString(),
      left_sections: leftSections,
      right_sections: rightSections,
      left_imu_accel: leftIMU.accel,
      left_imu_gyro: leftIMU.gyro,
      right_imu_accel: rightIMU.accel,
      right_imu_gyro: rightIMU.gyro,
      left_on_pointe: leftOnPointe,
      right_on_pointe: rightOnPointe,
      roll_throughs: rollThroughs,
      springs: springs,
      jump_landings: jumpLandings,
      last_transition_type: lastTransitionType,
      last_transition_confidence: lastTransitionConfidence,
      left_alignment_x: leftAlignX,
      left_alignment_y: leftAlignY,
      left_alignment_status: leftAlignStat,
      right_alignment_x: rightAlignX,
      right_alignment_y: rightAlignY,
      right_alignment_status: rightAlignStat,
      left_max_pressure: leftPressure.max,
      left_std_pressure: leftPressure.std,
      left_hotspot_count: leftPressure.hotspotCount,
      left_hotspot_regions: leftPressure.hotspotRegions,
      right_max_pressure: rightPressure.max,
      right_std_pressure: rightPressure.std,
      right_hotspot_count: rightPressure.hotspotCount,
      right_hotspot_regions: rightPressure.hotspotRegions,
    };

    dataService.dataBuffer.push(record);
    console.log('✅ Snapshot added, buffer size:', dataService.dataBuffer.length);

    if (dataService.dataBuffer.length >= dataService.BATCH_SIZE) {
      dataService.flushBuffer();
    }
  },

  flushBuffer: async () => {
    if (dataService.dataBuffer.length === 0) return;

    const toInsert = [...dataService.dataBuffer];
    dataService.dataBuffer = [];

    try {
      const { error } = await supabase
        .from('session_snapshots')
        .insert(toInsert);

      if (error) throw error;

      console.log(`📊 Saved ${toInsert.length} snapshots`);
    } catch (error) {
      console.error('Failed to save snapshots:', error);
      dataService.dataBuffer = [...toInsert, ...dataService.dataBuffer];
    }
  },

  getSessionData: async (sessionId) => {
    const { data, error } = await supabase
      .from('session_snapshots')
      .select('*')
      .eq('session_id', sessionId)
      .order('timestamp', { ascending: true });

    if (error) throw error;
    return data;
  },

  getSessions: async (userId) => {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('user_id', userId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  getSessionsWithStats: async (userId) => {
    const { data: sessions, error: sessionsError } = await supabase
      .from('sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('started_at', { ascending: false });

    if (sessionsError) throw sessionsError;
    if (!sessions || sessions.length === 0) return [];

    const sessionsWithStats = await Promise.all(
      sessions.map(async (session) => {
        const { data: snapshots, error: snapshotsError } = await supabase
          .from('session_snapshots')
          .select(`
            left_on_pointe,
            right_on_pointe,
            roll_throughs,
            springs,
            jump_landings,
            left_alignment_status,
            right_alignment_status
          `)
          .eq('session_id', session.id);

        if (snapshotsError) {
          console.error('Error fetching snapshots:', snapshotsError);
          return {
            ...session,
            stats: {
              foot: 'unknown',
              timeOnPointe: 0,
              rollThroughs: 0,
              springs: 0,
              jumpLandings: 0,
              goodCount: 0,
              okCount: 0,
              badCount: 0,
            },
          };
        }

        const hasLeftData = snapshots.some(s => s.left_on_pointe !== null);
        const hasRightData = snapshots.some(s => s.right_on_pointe !== null);

        let foot = 'unknown';
        if (hasLeftData && hasRightData) {
          foot = 'both';
        } else if (hasLeftData) {
          foot = 'left';
        } else if (hasRightData) {
          foot = 'right';
        }

        const timeOnPointe = snapshots.filter(
          s => s.left_on_pointe || s.right_on_pointe
        ).length;

        const lastSnapshot = snapshots[snapshots.length - 1];
        const rollThroughs = lastSnapshot?.roll_throughs || 0;
        const springs = lastSnapshot?.springs || 0;
        const jumpLandings = lastSnapshot?.jump_landings || 0;

        let goodCount = 0;
        let okCount = 0;
        let badCount = 0;

        snapshots.forEach(s => {
          if (s.left_on_pointe) {
            if (s.left_alignment_status === 'good') goodCount++;
            else if (s.left_alignment_status === 'ok') okCount++;
            else if (s.left_alignment_status === 'bad') badCount++;
          }
          if (s.right_on_pointe) {
            if (s.right_alignment_status === 'good') goodCount++;
            else if (s.right_alignment_status === 'ok') okCount++;
            else if (s.right_alignment_status === 'bad') badCount++;
          }
        });

        return {
          ...session,
          stats: {
            foot,
            timeOnPointe,
            rollThroughs,
            springs,
            jumpLandings,
            goodCount,
            okCount,
            badCount,
          },
        };
      })
    );

    return sessionsWithStats;
  },

  exportSessionCSV: async (sessionId) => {
    const data = await dataService.getSessionData(sessionId);
    if (!data || data.length === 0) return '';

    const headers = [
      'timestamp',
      'left_toe', 'left_metatarsal', 'left_front_left', 'left_front_mid',
      'left_front_right', 'left_back_left', 'left_back_mid', 'left_back_right',
      'right_toe', 'right_metatarsal', 'right_front_left', 'right_front_mid',
      'right_front_right', 'right_back_left', 'right_back_mid', 'right_back_right',
      'left_accel_x', 'left_accel_y', 'left_accel_z',
      'left_gyro_x', 'left_gyro_y', 'left_gyro_z',
      'right_accel_x', 'right_accel_y', 'right_accel_z',
      'right_gyro_x', 'right_gyro_y', 'right_gyro_z',
      'left_on_pointe', 'right_on_pointe',
      'roll_throughs', 'springs', 'jump_landings',
      'last_transition_type', 'last_transition_confidence',
      'left_align_x', 'left_align_y', 'left_align_status',
      'right_align_x', 'right_align_y', 'right_align_status',
      'left_max_pressure', 'left_std_pressure', 'left_hotspots',
      'right_max_pressure', 'right_std_pressure', 'right_hotspots'
    ];

    const rows = data.map(row => [
      row.timestamp,
      row.left_sections?.toe || 0, row.left_sections?.metatarsal || 0,
      row.left_sections?.frontLeft || 0, row.left_sections?.frontMid || 0,
      row.left_sections?.frontRight || 0, row.left_sections?.backLeft || 0,
      row.left_sections?.backMid || 0, row.left_sections?.backRight || 0,
      row.right_sections?.toe || 0, row.right_sections?.metatarsal || 0,
      row.right_sections?.frontLeft || 0, row.right_sections?.frontMid || 0,
      row.right_sections?.frontRight || 0, row.right_sections?.backLeft || 0,
      row.right_sections?.backMid || 0, row.right_sections?.backRight || 0,
      ...(row.left_imu_accel || [0, 0, 0]),
      ...(row.left_imu_gyro || [0, 0, 0]),
      ...(row.right_imu_accel || [0, 0, 0]),
      ...(row.right_imu_gyro || [0, 0, 0]),
      row.left_on_pointe ? 1 : 0,
      row.right_on_pointe ? 1 : 0,
      row.roll_throughs || 0,
      row.springs || 0,
      row.jump_landings || 0,
      row.last_transition_type || '',
      row.last_transition_confidence || '',
      row.left_alignment_x, row.left_alignment_y, row.left_alignment_status,
      row.right_alignment_x, row.right_alignment_y, row.right_alignment_status,
      row.left_max_pressure, row.left_std_pressure, row.left_hotspot_count,
      row.right_max_pressure, row.right_std_pressure, row.right_hotspot_count
    ]);

    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  },

  downloadCSV: async (sessionId, sessionName) => {
    const csv = await dataService.exportSessionCSV(sessionId);
    if (!csv) return;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionName || 'session'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  updateSessionName: async (sessionId, newName) => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .update({ name: newName })
        .eq('id', sessionId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Session name updated:', newName);
      return data;
    } catch (error) {
      console.error('Failed to update session name:', error);
      throw error;
    }
  },

  deleteSession: async (sessionId) => {
    try {
      await supabase
        .from('session_snapshots')
        .delete()
        .eq('session_id', sessionId);

      const { error } = await supabase
        .from('sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      console.log('✅ Session deleted:', sessionId);
    } catch (error) {
      console.error('Failed to delete session:', error);
      throw error;
    }
  }
};
